import { ActionIcon, Alert, Box, Button, Flex, Group, MultiSelect, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SectionProps } from '../../types';
import classes from './Metrics.module.css';

export default function MetricsSection({ getAvailableMeasures, updateConfig, config, validationErrors }: SectionProps ) {
  const { t } = useTranslation( 'widgets' );
  const measures = ( config.measures as string[]) || [];
  const sectionErrors = validationErrors?.metrics;

  const availableMeasures = getAvailableMeasures();

  const removeMeasure = ( measureToRemove: string ) => {
    const newMeasures = measures.filter( m => m !== measureToRemove );
    updateConfig( 'measures', newMeasures );
  };

  return (
    <div className={classes.container}>
      <Stack gap="md">
        <Title order={4} className={classes.sectionTitle}>
          {t( 'widget_builder_metrics_title','Metrics' )}
        </Title>
        <Alert icon={<IconInfoCircle size="1.2rem" />} color="blue.5" variant="light" mb="xs">
          <Text size='sm'>
            {t( 'widget_builder_metrics_description','Select the measures to display in your widget. Measures are pre-defined calculations from your data source.' )}
          </Text>
        </Alert>
        {sectionErrors && Object.keys( sectionErrors ).length > 0 && (
          <Alert icon={<IconAlertCircle size="1.2rem" />} color="red" variant="light" mb="xs">
            <Text size='sm' c="red.9">
              {t( 'widget_builder_metrics_errors_message','Please fix the following errors:' )}
              <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                {Object.entries( sectionErrors ).map(([ field, error ]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </Text>
          </Alert>
        )}
        <Box className={classes.content}>
          <MultiSelect
            label={t( 'widget_builder_metrics_select_label', 'Measures' )}
            placeholder={t( 'widget_builder_metrics_select_placeholder', 'Select measures' )}
            data={availableMeasures}
            value={measures}
            onChange={( value ) => updateConfig( 'measures', value )}
            searchable
            clearable
            checkIconPosition="right"
          />

          {measures.length > 0 && (
            <Paper p="sm" mt="md" withBorder>
              <Text size="sm" fw={500} mb="xs">
                {t( 'widget_builder_metrics_selected_label', 'Selected measures' )}:
              </Text>
              {measures.map( measure => {
                const memberName = measure.split( '.' )[1] || measure;
                return (
                  <Flex key={measure} justify="space-between" align="center" mb="xs">
                    <Text size="sm">{memberName}</Text>
                    <ActionIcon
                      color="danger.6"
                      variant="subtle"
                      onClick={() => removeMeasure( measure )}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Flex>
                );
              })}
            </Paper>
          )}
        </Box>
      </Stack>
    </div>
  );
}
